import { Card, NetworkImage, Typography } from "@/components/atoms";
// PhoneCallIcon: sales agent contact hidden for now
import { AppointmentCalendarIcon, MoreVerticalIcon } from "@/components/atoms/icons";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { useTranslation, useUIColors } from "@/core/hooks";
import { getShadowStyle } from "@/core/utils/shadowStyles";
// import { openPhoneDialer } from "@/core/utils/phoneUtils"; // Sales agent contact hidden for now
import React from "react";
import { Pressable, StyleSheet, View } from "react-native";

const THUMB_SIZE = 44;

export interface UpcomingAppointmentCardProps {
  /** Card heading — defaults to "Upcoming Appointment" when omitted, so
   * the same card can be reused for other statuses (e.g. "Unit Reserved"). */
  title?: string;
  /** Unit/property thumbnail — falls back to the calendar icon when omitted. */
  image?: string | null;
  /** Overrides both `image` and the default calendar icon (e.g. the Unit
   * Reserved confetti gif). */
  icon?: React.ReactNode;
  /** Unit/property name, e.g. "A1-401, Al Metlaa" — omit to keep the original
   * appointment-card layout (no name line). */
  name?: string;
  /** e.g. "5 August 2026" — omit for cards with no date/time (e.g. Reserved Unit) */
  date?: string;
  /** e.g. "12:30 PM" */
  time?: string;
  // Sales agent contact hidden for now
  // /** Sales agent name from the appointment API response, shown below date/time. */
  // agentName?: string | undefined;
  // /** Sales agent phone, when the API sends one — tappable to dial. */
  // agentPhone?: string | undefined;
  /** Extra status line below the name, e.g. "Awaiting your signature" */
  note?: string;
  /** e.g. "Tomorrow" / "Today" / "Awaiting Signature" — omit to hide the badge entirely. */
  badgeLabel?: string;
  /** Overrides the default red badge — used for reservation-status tone colors
   * (Pending/In Progress/Confirmed). Omit to keep the default red badge. */
  badgeBackgroundColor?: string;
  badgeTextColor?: string;
  onMenuPress?: () => void;
  /** Makes the whole card tappable (e.g. opens the Reservation Summary sheet). */
  onPress?: () => void;
}

function AppointmentCardLeading({
  icon,
  image,
  thumbBackgroundColor,
}: Readonly<{
  icon?: React.ReactNode | undefined;
  image?: string | null | undefined;
  thumbBackgroundColor: string;
}>) {
  if (icon) {
    return <>{icon}</>;
  }
  if (image) {
    return (
      <View style={[styles.thumb, { backgroundColor: thumbBackgroundColor }]}>
        <NetworkImage url={image} style={styles.thumbImage} resizeMode="cover" />
      </View>
    );
  }
  return <AppointmentCalendarIcon size={44} />;
}

/* Sales agent contact hidden for now
function AppointmentCardAgentRow({
  agentName,
  agentPhone,
}: Readonly<{
  agentName?: string | undefined;
  agentPhone?: string | undefined;
}>) {
  if (!agentName && !agentPhone) {
    return null;
  }

  const handleAgentNamePress = agentPhone ? () => openPhoneDialer(agentPhone) : undefined;
  const agentNameAccessibilityProps = agentPhone ? { accessibilityLabel: agentPhone } : {};

  return (
    <View style={styles.agentRow}>
      {agentPhone ? (
        <Pressable
          onPress={() => openPhoneDialer(agentPhone)}
          hitSlop={4}
          accessibilityRole="button"
          accessibilityLabel={agentPhone}
        >
          <PhoneCallIcon size={14} color={UI_PRIMITIVES.redLight} />
        </Pressable>
      ) : null}
      {agentName ? (
        <Pressable
          onPress={handleAgentNamePress}
          disabled={!agentPhone}
          hitSlop={4}
          accessibilityRole="button"
          {...agentNameAccessibilityProps}
        >
          <Typography
            numberOfLines={1}
            style={[styles.dateTime, styles.agentName, { color: UI_PRIMITIVES.redLight }]}
          >
            {agentName}
          </Typography>
        </Pressable>
      ) : null}
    </View>
  );
}
*/

function AppointmentCardBadge({
  badgeLabel,
  badgeBackgroundColor,
  badgeTextColor,
}: Readonly<{
  badgeLabel?: string | undefined;
  badgeBackgroundColor?: string | undefined;
  badgeTextColor?: string | undefined;
}>) {
  if (!badgeLabel) {
    return null;
  }
  return (
    <View
      style={[
        styles.badge,
        { backgroundColor: badgeBackgroundColor ?? UI_PRIMITIVES.redLightest2 },
      ]}
    >
      <Typography style={[styles.badgeText, { color: badgeTextColor ?? UI_PRIMITIVES.redDefault }]}>
        {badgeLabel}
      </Typography>
    </View>
  );
}

export function UpcomingAppointmentCard({
  title,
  image,
  icon,
  name,
  date,
  time,
  // Sales agent contact hidden for now
  // agentName,
  // agentPhone,
  note,
  badgeLabel,
  badgeBackgroundColor,
  badgeTextColor,
  onMenuPress,
  onPress,
}: Readonly<UpcomingAppointmentCardProps>) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const CardWrapper = onPress ? Pressable : View;

  return (
    <CardWrapper {...(onPress ? { onPress } : {})}>
    <Card
      variant="default"
      style={[
        styles.card,
        getShadowStyle({ ...styles.cardShadow, shadowColor: colors.fontPrimary }),
        { backgroundColor: colors.backgroundLevel1 },
      ]}
    >
      <View style={styles.row}>
        <AppointmentCardLeading
          icon={icon}
          image={image}
          thumbBackgroundColor={colors.backgroundLevel2}
        />

        <View style={styles.content}>
          <Typography color="primary" numberOfLines={1} style={styles.title}>
            {title ?? t("bookAppointment.upcomingAppointment")}
          </Typography>
          {date && time ? (
            <Typography color="secondary" style={styles.dateTime}>
              {/* LTR isolate (U+2066…U+2069) keeps "9:00 AM" in order inside Arabic,
                  which would otherwise reorder it to "AM 9:00". */}
              {date} • {`\u2066${time}\u2069`}
            </Typography>
          ) : null}
          {/* Sales agent contact hidden for now
          <AppointmentCardAgentRow agentName={agentName} agentPhone={agentPhone} />
          */}
          {name ? (
            <Typography
              color="primary"
              numberOfLines={1}
              ellipsizeMode="tail"
              style={styles.name}
            >
              {name}
            </Typography>
          ) : null}
          {note ? (
            <Typography color="secondary" numberOfLines={1} style={styles.dateTime}>
              {note}
            </Typography>
          ) : null}
        </View>

        <AppointmentCardBadge
          badgeLabel={badgeLabel}
          badgeBackgroundColor={badgeBackgroundColor}
          badgeTextColor={badgeTextColor}
        />

        {onMenuPress ? (
          <Pressable onPress={onMenuPress} hitSlop={8} style={styles.menuButton} accessibilityRole="button">
            <MoreVerticalIcon size={20} color={colors.fontTertiary} />
          </Pressable>
        ) : null}
      </View>
    </Card>
    </CardWrapper>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: 12,
    borderRadius: 16,
  },
  cardShadow: {
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  rowRTL: {
    flexDirection: "row-reverse",
  },
  thumb: {
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: 10,
    overflow: "hidden",
  },
  thumbImage: {
    width: "100%",
    height: "100%",
  },
  content: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  title: {
    flexShrink: 1,
    fontFamily: "Matter-Medium",
    fontWeight: "500",
    fontSize: 12,
    lineHeight: 12,
    letterSpacing: 0,
  },
  dateTime: {
    fontFamily: "Matter-Regular",
    fontWeight: "400",
    fontSize: 10,
    lineHeight: 10,
    letterSpacing: 0,
  },
  agentRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  // Font/Family, Font/Size/3XS Regular, weight 400, LH 100%, letter-spacing 0
  agentName: {
    flexShrink: 1,
    fontSize: 12,
    lineHeight: 12,
  },
  name: {
    flexShrink: 1,
    fontFamily: "Matter-SemiBold",
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 14,
    letterSpacing: 0,
  },
  badge: {
    flexShrink: 0,
    minHeight: 22,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: {
    fontFamily: "Matter-SemiBold",
    fontWeight: "600",
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 0,
  },
  menuButton: {
    padding: 4,
  },
});

export default UpcomingAppointmentCard;
